/**
 * Company: SPHMedia
 * Description: [Location validation hook for Food Map page]
 */

import { isLocationInSingapore } from "@helper/utils";
import { Point, PositionError } from "@typings/foodMapTypes";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

type useLocationValidationData = {
  userLocation: Point | undefined;
  watchLocation: Point | undefined;
  geoError: PositionError | GeolocationPositionError | undefined;
  startWatching: boolean;
  setUserLocation: Dispatch<SetStateAction<Point | undefined>>;
  setWatchLocation: Dispatch<SetStateAction<Point | undefined>>;
  setGeoError: Dispatch<
    SetStateAction<PositionError | GeolocationPositionError | undefined>
  >;
};

type useLocationValidationProps = {
  location: Point | null;
  bhCustomEvent: ReturnType<
    (typeof import("@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall"))["useBHCustomEventAnalyticsCall"]
  >;
  virtualPVEvent: ReturnType<
    (typeof import("@hooks/useAnalyticsCall/useVirtualPVAnalyticsCall"))["useVirtualPVAnalyticsCall"]
  >;
  error: PositionError | GeolocationPositionError | null;
};

/**
 * Custom React Hook: useLocationValidationHook
 * Description: [Validates user location for Food Map page]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useLocationValidation({
  location,
  bhCustomEvent,
  virtualPVEvent,
  error,
}: useLocationValidationProps): useLocationValidationData {
  const [userLocation, setUserLocation] = useState<Point | undefined>();
  const [watchLocation, setWatchLocation] = useState<Point>();
  const [geoError, setGeoError] = useState<
    PositionError | GeolocationPositionError
  >();
  const [initLocation, setInitLocation] = useState<boolean>(false);
  const [startWatching, setStartWatching] = useState(false);

  const initializeLocation = useCallback(
    (loc: Point) => {
      setUserLocation(loc);
      setWatchLocation(loc);
      setInitLocation(true);
      setStartWatching(true);
      bhCustomEvent("foodmap", "initialize", undefined, false);
      virtualPVEvent("/foodmap", "Food Map List", `${loc.lat}, ${loc.lng}`);
    },
    [bhCustomEvent, virtualPVEvent],
  );

  useEffect(() => {
    if (initLocation) return;

    if (!location) return;

    if (error) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setGeoError(error);
      return;
    }

    if (!isLocationInSingapore(location)) {
      setWatchLocation(location);
      setGeoError({
        code: 404,
        message: "Location out of Singapore",
      });
      return;
    }

    initializeLocation(location);
  }, [initLocation, location, error, initializeLocation]);

  // Return the data or any functions required by the component using this hook
  return {
    userLocation,
    watchLocation,
    geoError,
    startWatching,
    setUserLocation,
    setWatchLocation,
    setGeoError,
  };
}
